import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, User, Image as ImageIcon, GripVertical, Save, Sparkles } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const MAX_PHOTOS = 10;

const emptyRecord = {
  id: "",
  name: "",
  description: "",
  photo_url: "",
  photo_public_id: "",
  photos: [],
  order: 0,
  status: "Active",
};

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">{label}</div>
        {hint && <div className="text-[10px] text-[#8a8478]">{hint}</div>}
      </div>
      {children}
    </label>
  );
}

export default function Pioneers() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor"].includes(role);
  const [record, setRecord] = useState(emptyRecord);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchOne = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pioneers");
      const first = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (first) {
        setRecord({
          id: first.id || "",
          name: first.name || "",
          description: first.description || "",
          photo_url: first.photo_url || "",
          photo_public_id: first.photo_public_id || "",
          photos: Array.isArray(first.photos) ? first.photos.slice(0, MAX_PHOTOS) : [],
          order: 0,
          status: first.status || "Active",
        });
      } else {
        setRecord(emptyRecord);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchOne(); }, []);

  const set = (k, v) => setRecord((r) => ({ ...r, [k]: v }));

  const gallery = record.photos || [];
  const remaining = useMemo(() => Math.max(0, MAX_PHOTOS - gallery.length), [gallery.length]);

  const addPhoto = ({ url, public_id }) => {
    if (!url) return;
    if (gallery.length >= MAX_PHOTOS) {
      toast.error(`You can add up to ${MAX_PHOTOS} photos only`);
      return;
    }
    setRecord((r) => ({ ...r, photos: [...(r.photos || []), { url, public_id: public_id || "", caption: "" }] }));
  };

  const removePhoto = (idx) => {
    setRecord((r) => ({ ...r, photos: (r.photos || []).filter((_, i) => i !== idx) }));
  };

  const updateCaption = (idx, caption) => {
    setRecord((r) => ({ ...r, photos: (r.photos || []).map((p, i) => (i === idx ? { ...p, caption } : p)) }));
  };

  const movePhoto = (from, to) => {
    if (to < 0 || to >= gallery.length) return;
    setRecord((r) => {
      const next = [...(r.photos || [])];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return { ...r, photos: next };
    });
  };

  const save = async () => {
    if (!record.name.trim()) return toast.error("Founder name is required");
    const payload = {
      name: record.name.trim(),
      description: record.description || "",
      photo_url: record.photo_url || "",
      photo_public_id: record.photo_public_id || "",
      photos: (record.photos || []).slice(0, MAX_PHOTOS).map((p) => ({
        url: p.url || "",
        public_id: p.public_id || "",
        caption: (p.caption || "").trim().slice(0, 160),
      })),
      order: 0,
      status: record.status === "Inactive" ? "Inactive" : "Active",
    };
    setSaving(true);
    try {
      if (record.id) {
        await api.put(`/admin/pioneers/${record.id}`, payload);
        toast.success("Founder Inspiration updated");
      } else {
        const { data } = await api.post("/admin/pioneers", payload);
        if (data?.id) setRecord((r) => ({ ...r, id: data.id }));
        toast.success("Founder Inspiration saved");
      }
      fetchOne();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-founder-inspiration">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17] flex items-center gap-2">
            <Sparkles size={18} className="text-[#D4A24C]" /> Founder Inspiration
          </h1>
          <div className="text-sm text-[#6b655b] mt-0.5 max-w-2xl">
            The single record shown on the public <span className="font-semibold">Founder Inspiration</span> page — includes the founder&apos;s biography (rich text) and up to {MAX_PHOTOS} photographs displayed as a slider.
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="adm-founder-save-btn"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-5 py-2.5 text-sm font-semibold rounded-sm transition-colors disabled:opacity-60"
          >
            <Save size={15} strokeWidth={1.8} />
            {saving ? "Saving…" : (record.id ? "Update" : "Save")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Left: main content */}
          <div className="space-y-6">
            {/* Bio card */}
            <div className="bg-white rounded-sm border border-[#e5e0d8] p-6">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Founder Name *">
                  <input data-testid="adm-founder-input-name" type="text" maxLength={160} className={inputCls} value={record.name} onChange={(e) => set("name", e.target.value)} placeholder="Dr. Yadnaveer Kawade" />
                </Field>
                <Field label="Status">
                  <select data-testid="adm-founder-input-status" className={inputCls} value={record.status} onChange={(e) => set("status", e.target.value)}>
                    <option value="Active">Active — visible on public site</option>
                    <option value="Inactive">Inactive — hidden</option>
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Biography · Inspiration</div>
                  <div className="text-[10px] text-[#8a8478]">Rich text · formatting, links, headings supported</div>
                </div>
                <div data-testid="adm-founder-input-desc">
                  <RichTextEditor
                    value={record.description || ""}
                    onChange={(html) => set("description", html || "")}
                    placeholder="Share the founder's story, philosophy, quotes, milestones…"
                    minHeight={340}
                  />
                </div>
              </div>
            </div>

            {/* Photo Gallery */}
            <div className="bg-white rounded-sm border border-[#e5e0d8] p-6" data-testid="adm-founder-gallery">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Photo Slider</div>
                  <div className="text-sm text-[#1c1a17] font-semibold mt-0.5">{gallery.length} of {MAX_PHOTOS} photos added</div>
                  <div className="text-[11px] text-[#8a8478] mt-1">Photos rotate as a slider on the public Founder Inspiration page. Drag order using the arrows below each photo.</div>
                </div>
                {canEdit && remaining > 0 && (
                  <div className="shrink-0">
                    <FileUploader
                      kind="gallery_photo"
                      label={`add photo (${remaining} left)`}
                      onChange={({ url, public_id }) => { addPhoto({ url, public_id }); }}
                      testid="adm-founder-photo-upload"
                    />
                  </div>
                )}
              </div>

              {gallery.length === 0 ? (
                <div className="border-2 border-dashed border-[#e5e0d8] rounded-sm p-10 text-center">
                  <ImageIcon size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
                  <div className="mt-3 text-sm text-[#6b655b]">No photos yet. Add up to {MAX_PHOTOS} portraits, moments, or memorabilia.</div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {gallery.map((p, i) => (
                    <div key={`${p.public_id || p.url}-${i}`} data-testid={`adm-founder-photo-${i}`} className="border border-[#e5e0d8] rounded-sm overflow-hidden bg-[#faf7f0]">
                      <div className="aspect-[4/5] bg-[#f5f1e8] relative overflow-hidden">
                        <img src={p.url} alt={p.caption || `photo ${i + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">#{i + 1}</div>
                      </div>
                      <div className="p-2 space-y-2">
                        <input
                          type="text"
                          value={p.caption || ""}
                          onChange={(e) => updateCaption(i, e.target.value)}
                          maxLength={160}
                          data-testid={`adm-founder-photo-caption-${i}`}
                          placeholder="Caption (optional)"
                          className="w-full text-xs bg-white border border-[#e5e0d8] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#234333]"
                        />
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-0.5">
                            <button type="button" onClick={() => movePhoto(i, i - 1)} disabled={i === 0} className="p-1 rounded-sm hover:bg-[#e5e0d8] disabled:opacity-30" title="Move earlier" data-testid={`adm-founder-photo-up-${i}`}><GripVertical size={12} className="rotate-90" /></button>
                            <button type="button" onClick={() => movePhoto(i, i + 1)} disabled={i === gallery.length - 1} className="p-1 rounded-sm hover:bg-[#e5e0d8] disabled:opacity-30" title="Move later" data-testid={`adm-founder-photo-down-${i}`}><GripVertical size={12} className="-rotate-90" /></button>
                          </div>
                          <button type="button" onClick={() => removePhoto(i)} data-testid={`adm-founder-photo-remove-${i}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[10px] font-semibold px-2 py-1 rounded-sm">
                            <Trash2 size={11} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {remaining > 0 && canEdit && (
                    <div className="border-2 border-dashed border-[#e5e0d8] rounded-sm aspect-[4/5] flex items-center justify-center bg-[#faf7f0]">
                      <div className="text-center px-3">
                        <Plus size={22} className="mx-auto text-[#bcb6aa]" strokeWidth={1.5} />
                        <div className="text-[11px] text-[#8a8478] mt-1.5">{remaining} slot{remaining > 1 ? "s" : ""} remaining</div>
                        <div className="text-[10px] text-[#bcb6aa] mt-0.5">Use the button above to upload</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: primary portrait + preview */}
          <aside className="space-y-4">
            <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Primary Portrait</div>
              <div className="text-[11px] text-[#8a8478] mt-1">The lead photo shown alongside the biography (used as the slider&apos;s first slide when no gallery photos are set).</div>
              <div className="mt-3 aspect-[4/5] bg-[#f5f1e8] rounded-sm overflow-hidden flex items-center justify-center">
                {record.photo_url ? (
                  <img src={record.photo_url} alt="portrait" className="w-full h-full object-cover" />
                ) : (
                  <User size={44} className="text-[#bcb6aa]" strokeWidth={1.2} />
                )}
              </div>
              <div className="mt-3">
                <FileUploader
                  kind="photo"
                  label="portrait"
                  url={record.photo_url}
                  onChange={({ url, public_id }) => { set("photo_url", url || ""); set("photo_public_id", public_id || ""); }}
                  testid="adm-founder-primary-photo"
                />
              </div>
            </div>

            <div className="bg-[#faf7f0] rounded-sm border border-[#e5e0d8] p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Tips</div>
              <ul className="mt-2 text-[12px] text-[#3E3E3E] space-y-1.5 list-disc pl-4">
                <li>Portraits and event photos work great as slider images.</li>
                <li>Add captions (year, place or context) — they appear as an overlay on the public site.</li>
                <li>Order matters — the first photo is shown by default; others follow.</li>
                <li>Set status to <em>Inactive</em> to temporarily hide the entire section.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
